import React, { Fragment, useCallback, useMemo, useState } from "react";
import CustomTabs from "../../../components/CustomTabs/CustomTabs";
import StatusTextTag from "../../../components/StatusTextTag/StatusTextTag";
import { IconButton } from "@mui/material";
import {
  APROVED_HEADERS,
  PENDING_HEADERS,
  REPROVED_HEADERS,
  TabsHeaderLabel,
} from "../constants/table-headers";
import TablePaginated, {
  Column,
} from "../../../components/TablePaginated/TablePaginated";
import { EditOutlined, Visibility } from "@mui/icons-material";
import { routesConfig } from "../../../config/routes";
import {
  ROWS_PER_PAGE,
  useStorageContext,
} from "../../../contexts/storage/storage-provider";
import {
  convertToDefaultDate,
  formatDateWithoutTimezone,
} from "../../../utils/dateFormatter";
import { useNavigate } from "react-router-dom";
import { PipelineScheduleStatus } from "../../../modules/operational/models/GetPipelineSchedulesDTO";
import { Row } from "../../../modules/storage/models/storage-dto";
import CardDataNotFound from "../../../components/CardDataNotFound/CardDataNotFound";
import { applyVolumeMask, getThousand } from "../../../utils";
import { TabsDescription } from "../constants/tabs-description";
import { EditModal } from "./EditModal";

const RiverDischargesTable = React.memo(() => {
  const {
    filters,
    setFilters,
    tab,
    setTab,
    list,
    fetchList,
    loadingData,
    setStatusVendor,
  } = useStorageContext();

  const navigate = useNavigate();

  const [data, setData] = useState<Row | null>(null);
  const [showView, setShowView] = useState<boolean>(false);

  const { hash } = window.location;
  const slug = hash.split("#")[1];

  const isDischarge = slug === routesConfig.STORAGE().RIVER_DISCHARGES;

  const handlePageChange = (page: number) => {
    setFilters((oldState) => ({
      ...oldState,
      page: page,
    }));
    fetchList({ ...filters, page: page });
  };

  const handleSetHeaders: Column[] = useMemo(() => {
    switch (tab) {
      case TabsDescription.VENDOR_PENDING:
        return PENDING_HEADERS;
      case TabsDescription.STORAGE_APPROVED:
        return APROVED_HEADERS;
      case TabsDescription.VENDOR_REJECT:
        return isDischarge
          ? REPROVED_HEADERS.filter(
              (item) => item.id !== "suggestedVolume" && item.id !== "ov"
            )
          : REPROVED_HEADERS;
      default:
        return [];
    }
  }, [tab]);

  const handleAction = (data: {
    id: string;
    status: PipelineScheduleStatus;
  }) => {
    navigate(
      `${
        routesConfig.STORAGE()[
          isDischarge ? "CREATE_SCHEDULING" : "CREATE_PUMPING_REQUEST"
        ]
      }/${data.id}`
    );
  };

  const handleClose = () => {
    setShowView(false);
  };

  const handleActionView = (item: Row) => {
    setData(item);
    setShowView(true);
  };

  const handleChangeTab = (event: number) => {
    let status: PipelineScheduleStatus;

    switch (event) {
      case TabsDescription.VENDOR_PENDING:
        status = PipelineScheduleStatus.VENDOR_PENDING;
        break;
      case TabsDescription.STORAGE_APPROVED:
        status = PipelineScheduleStatus.STORAGE_APPROVED;
        break;
      case TabsDescription.VENDOR_REJECT:
        status = PipelineScheduleStatus.VENDOR_REJECTED;
        break;
      default:
        return;
    }

    setTab(event);
    setStatusVendor(status);
  };

  const renderDynamicRows = useCallback(
    (item: Row) => {
      const status = <StatusTextTag value={item.status} />;
      const action = !tab ? (
        <IconButton
          onClick={() => handleAction({ id: item.id, status: item.status })}
        >
          <EditOutlined />
        </IconButton>
      ) : (
        <IconButton onClick={() => handleActionView(item)}>
          <Visibility />
        </IconButton>
      );

      return {
        ...item,
        scheduleDate: convertToDefaultDate(item.scheduleDate),
        scheduleTime:
          item.scheduleTime ??
          formatDateWithoutTimezone(item.scheduleDate, "HH:mm"),
        externalId: item.contract.customerCompany.externalId ?? "-",
        client: item.contract.customerCompany.description,
        branch: item.contract.subsidiary.description,
        product: item.contract.productId,
        modality: item.contract.mod,
        measurementUnit: item.contract.product.measurementUnit,
        volumeRequested: getThousand(item.volumeRequested),
        volumeProvided: getThousand(item.volumeProvided),
        ov: item.externalId,
        ovVolume: applyVolumeMask(String(item.externalVolume ?? 0)),
        status,
        action,
      };
    },
    [tab]
  );

  return (
    <Fragment>
      <CustomTabs
        tabsHeader={Object.values(TabsHeaderLabel)}
        onChangeTab={(event) => handleChangeTab(Number(event))}
      />

      {showView && data && (
        <EditModal
          data={data}
          open={showView}
          onClose={handleClose}
          isViewing={!!tab}
        />
      )}

      {!loadingData && !list.rows.length ? (
        <CardDataNotFound
          icon="cactus"
          title="Não foram encontradas programações"
          subtitle="Informe outros filtros ou abas e tente novamente"
        />
      ) : (
        <TablePaginated
          isLoadingData={loadingData}
          columns={handleSetHeaders}
          data={list.rows.map((item) => renderDynamicRows(item))}
          rowsPerPage={ROWS_PER_PAGE}
          paginationProps={{
            page: filters.page,
            pagesTotal: Math.ceil(list.count / ROWS_PER_PAGE),
          }}
          onChangePage={handlePageChange}
          loadFromServer
        />
      )}
    </Fragment>
  );
});

export { RiverDischargesTable };
